import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import type {
  ActiveFilter,
  ApplyFilterValueProps,
  Filter,
  FiltersForTable,
  SearchTextFilterProps,
} from '../store';
import { useFiltersStore } from '../store/filters.store';
import type { ActiveFilterModelValue } from '../store/filters.types.ts';
import { FilterTypes } from '../utils';
import { trackFiltersTableRead } from './filters-table-reads';

/**
 * This composable works like an utility hook to manage filters for a specific namespace.
 * Once it is initialized, you can operate directly on that filter namespace.
 * @param filterNamespaceSource
 * @returns
 */
export function useFilters(
  filterNamespaceSource?: MaybeRefOrGetter<string | undefined>,
) {
  const filtersStore = useFiltersStore();

  const getFilters = computed(() => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    return filtersStore.filters[filterNamespace];
  });

  const initializeFilters = () => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    filtersStore.initializeFilter(filterNamespace);
  };

  const addFilter = (newFilter: ActiveFilter) => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    filtersStore.addFilter(filterNamespace, newFilter);
  };

  const upsertFilter = (newFilter: ActiveFilter) => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    filtersStore.upsertFilter(filterNamespace, newFilter);
  };

  const sortFilters = (filterFieldNames: string[]) => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    filtersStore.sortFilters(filterNamespace, filterFieldNames);
  };

  const removeFilter = (filterFieldName: string) => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    filtersStore.removeFilter(filterNamespace, filterFieldName);
  };

  const resetAllfilters = () => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    filtersStore.initializeFilter(filterNamespace);
  };

  /*
    Compact filters are rendered inline by the top bar and must survive the
    reset, so they are blanked one by one instead of dropping the namespace.
  */
  const clearAllFilters = (persistent = false) => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    if (!persistent) {
      resetAllfilters();
      return;
    }
    filtersStore.filters[filterNamespace]?.forEach((filter) => {
      filtersStore.applyFilterValue({
        entityName: filterNamespace,
        filterFieldName: filter.filterFieldName,
        filterType: filter.filterType,
        value: undefined,
        needleBy: (filter as SearchTextFilterProps).selectedSearchBy,
      });
    });
  };

  const isFiltering = computed<boolean>(() => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return false;
    return (
      (filtersStore.filters[filterNamespace] &&
        filtersStore.filters[filterNamespace].filter(
          (filter) =>
            filter.filterType !== FilterTypes.TEXT_SEARCH &&
            filter.modelValue !== undefined,
        ).length > 0) ||
      false
    );
  });

  const isSearching = computed(() => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    const searchFilter = filtersStore.filters[filterNamespace]?.filter(
      (filter) => filter.filterType === FilterTypes.TEXT_SEARCH,
    )[0];
    return (
      filtersStore.filters[filterNamespace] &&
      searchFilter &&
      !!searchFilter.modelValue
    );
  });

  const isFilteringOrSearching = computed(() => {
    return isFiltering.value || isSearching.value;
  });

  const applyFilterValue = (payload: ApplyFilterValueProps) => {
    const { filterFieldName, filterType, value, toKey, fromKey, needleBy } =
      payload;
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    filtersStore.applyFilterValue({
      entityName: filterNamespace,
      filterFieldName,
      value,
      filterType,
      toKey,
      fromKey,
      needleBy,
    });
  };

  const filterIsActive = (filterFieldName: string): boolean | undefined => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    return filtersStore.filterIsActive(filterNamespace, filterFieldName);
  };

  /**
   * Extract the filters for the "filter" event emitted in the table.
   * Returns: Record<string, {
   filterType: FilterTypes;
   value: ActiveFilterModelValue;
   }
   >
   */
  const getFiltersForTable = computed(() => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    if (import.meta.env.DEV) trackFiltersTableRead(filterNamespace);
    const res: FiltersForTable = {};
    if (filtersStore.filters?.[filterNamespace]) {
      filtersStore.filters[filterNamespace].forEach((filter: Filter) => {
        const baseFilter = {
          ...filter,
          filterType: filter.filterType,
          value: filter.modelValue,
        };

        // Include needleBy for TEXT_SEARCH filters if present
        if (filter.filterType === FilterTypes.TEXT_SEARCH) {
          const searchFilter = filter as SearchTextFilterProps;
          if (searchFilter.selectedSearchBy) {
            res[filter.filterFieldName] = {
              ...baseFilter,
              needleBy: searchFilter.selectedSearchBy,
            };
            return;
          }
        }

        res[filter.filterFieldName] = baseFilter;
      });
    }
    return res;
  });

  const getFilterValue = (filterFieldName: string): ActiveFilterModelValue => {
    const filterNamespace = toValue(filterNamespaceSource);
    if (!filterNamespace) return;
    return getFiltersForTable.value?.[filterFieldName]?.value;
  };

  return {
    getFiltersForTable,
    applyFilterValue,
    filterIsActive,
    isFiltering,
    resetAllfilters,
    initializeFilters,
    getFilters,
    addFilter,
    upsertFilter,
    sortFilters,
    removeFilter,
    isSearching,
    getFilterValue,
    isFilteringOrSearching,
    clearAllFilters,
  };
}
